// One way for every Operations surface to read the API.
//
// WHY A HOOK RATHER THAN axios IN EACH SURFACE
// ============================================
// Five tabs read eleven endpoints between them, and every one of them has the
// same four outcomes: loading, data, empty, refused. Written per surface that
// becomes five slightly different opinions about what "empty" looks like and
// five places to forget the 403 case. R5 says the client's job is to agree with
// the backend rather than invent a second gate — which in practice means a
// founder-only endpoint returning 403 has to render as *refused*, visibly, not
// as an empty list that reads like "nothing is happening".
//
// NO NEW ENDPOINTS (R4)
// =====================
// Every URL these surfaces call already exists in `backend/routers/ops.py`.
// Nothing here composes, aggregates or invents one. Where a surface wants data
// no endpoint returns, it renders the empty state and the gap is written down as
// a finding — it is not a reason to touch the backend.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** The four states a surface can be in. Named, so no surface invents a fifth. */
export const IDLE = "idle";
export const LOADING = "loading";
export const READY = "ready";
export const REFUSED = "refused";
export const FAILED = "failed";

/**
 * Read one endpoint.
 *
 * `path` is relative to /api and must name a route that already exists.
 * Returns { status, data, error, reload } — `status` is one of the constants
 * above, never a boolean pair that can express "loading and ready at once".
 */
export function useEndpoint(path, { enabled = true } = {}) {
  const [status, setStatus] = useState(enabled ? LOADING : IDLE);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!enabled) return;
    setStatus(LOADING);
    setError(null);
    try {
      const res = await axios.get(`${API}${path}`);
      if (!alive.current) return;
      setData(res.data);
      setStatus(READY);
    } catch (err) {
      if (!alive.current) return;
      const code = err?.response?.status;
      // 401 and 403 are not failures of this surface. They are the backend
      // enforcing what it is supposed to enforce, and the honest rendering says
      // so rather than showing an empty panel that looks like calm.
      setStatus(code === 401 || code === 403 ? REFUSED : FAILED);
      setError(code ? `HTTP ${code}` : "unreachable");
    }
  }, [path, enabled]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  return { status, data, error, reload: load };
}

/** Read several endpoints at once, keeping each one's state separate. */
export function useEndpoints(paths) {
  // Deliberately not Promise.all: one refused endpoint must not blank the
  // surfaces beside it. A founder-only panel showing "refused" next to three
  // panels showing data is the accurate picture.
  const entries = {};
  for (const [key, path] of Object.entries(paths)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    entries[key] = useEndpoint(path);
  }
  return entries;
}

/** A number that might be absent. Never renders a fabricated zero. */
export function metric(value) {
  return value === null || value === undefined ? "—" : String(value);
}
